import { useCallback, useEffect, useMemo, useState } from "react";
import {
  casesService,
  extractDocTypesArrayFromGetServiceResponse,
  isDocTypeActiveFlag,
} from "services/cases.service";
import type { CaseDocumentTypeItem, CaseUploadedDocumentItem } from "services/cases.service";
import { useSessionData } from "helpers";
import { useViewerRoleContext } from "hooks/useViewerRoleContext";

export interface CaseDocumentRow extends CaseUploadedDocumentItem {
  doc_type_name?: string;
  doc_type_id?: number;
}

export function coerceDocumentNumericId(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.trim());
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export function getCaseDocumentCaseDocId(row: CaseDocumentRow): number | undefined {
  const r = row as Record<string, unknown>;
  return (
    coerceDocumentNumericId(r.case_doc_id) ??
    coerceDocumentNumericId(row.case_document_id) ??
    coerceDocumentNumericId(r.caseDocumentId)
  );
}

export function getCaseDocumentPrimaryId(row: CaseDocumentRow): number | undefined {
  const r = row as Record<string, unknown>;
  return (
    coerceDocumentNumericId(row.user_document_id) ??
    coerceDocumentNumericId(r.document_id) ??
    coerceDocumentNumericId(r.user_doc_id) ??
    coerceDocumentNumericId(r.document_user_id)
  );
}

export function getCaseDocumentTypeId(row: CaseDocumentRow): number | undefined {
  const r = row as Record<string, unknown>;
  return (
    coerceDocumentNumericId(row.doc_type_id) ??
    coerceDocumentNumericId(r.document_type_id) ??
    coerceDocumentNumericId(r.doctype_id)
  );
}

export function getCaseDocumentPlainPassword(row: CaseDocumentRow): string | null {
  const r = row as Record<string, unknown>;
  const candidates = [row.password, r.document_password, r.doc_password];
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    const s = String(c).trim();
    if (s !== "") return s;
  }
  return null;
}

export function coerceBizViewOnlyFlag(flag: unknown): boolean {
  if (flag === true) return true;
  if (flag === 1 || flag === "1") return true;
  if (typeof flag === "string") {
    const s = flag.trim().toLowerCase();
    if (s === "true" || s === "yes" || s === "y") return true;
  }
  return false;
}

export function getCaseDocumentBizViewOnly(row: CaseDocumentRow): boolean {
  const r = row as Record<string, unknown>;
  return coerceBizViewOnlyFlag(r.biz_view_only ?? r.bizViewOnly);
}

export function caseUploadToRow(
  cd: CaseDocumentTypeItem,
  d: CaseUploadedDocumentItem
): CaseDocumentRow {
  const docPath = d.document ?? (d as { file_key?: string }).file_key ?? "";
  const derivedName = docPath ? docPath.split("/").pop() : undefined;
  const dRec = d as Record<string, unknown>;
  const cdRec = cd as Record<string, unknown>;
  const parentDocTypeId =
    coerceDocumentNumericId(cd.doc_type_id) ??
    coerceDocumentNumericId(cdRec.document_type_id) ??
    coerceDocumentNumericId(cdRec.doctype_id);
  const userDocId =
    coerceDocumentNumericId(d.user_document_id) ??
    coerceDocumentNumericId(dRec.document_id) ??
    coerceDocumentNumericId(dRec.user_doc_id) ??
    coerceDocumentNumericId(dRec.document_user_id);
  const caseDocId =
    coerceDocumentNumericId(dRec.case_doc_id) ??
    coerceDocumentNumericId(d.case_document_id) ??
    coerceDocumentNumericId(dRec.caseDocumentId);
  const uploadedBy = (() => {
    const raw =
      (d as unknown as { uploadedBy?: unknown }).uploadedBy ??
      d.uploaded_by ??
      (d as { uploaded_by_name?: unknown }).uploaded_by_name ??
      (d as { user_name?: unknown }).user_name ??
      (d as { uploaded_user_name?: unknown }).uploaded_user_name ??
      (d as { created_by?: unknown }).created_by ??
      (dRec as { uploaded_by?: unknown }).uploaded_by ??
      "";

    if (typeof raw === "string" || typeof raw === "number") return String(raw);
    if (raw && typeof raw === "object") {
      const r = raw as Record<string, unknown>;
      const name =
        r.user_full_name ??
        r.full_name ??
        r.name ??
        r.username ??
        r.email ??
        r.user ??
        r.created_by;
      if (typeof name === "string" || typeof name === "number") return String(name);
    }
    return "";
  })();
  const uploadedOn =
    d.uploaded_on ??
    (d as { uploaded_on_date?: string }).uploaded_on_date ??
    (d as { upload_date?: string }).upload_date ??
    (d as { created?: string }).created ??
    (d as { created_at?: string }).created_at ??
    "";
  const rowDocTypeId =
    coerceDocumentNumericId(d.doc_type_id) ??
    coerceDocumentNumericId(dRec.document_type_id) ??
    coerceDocumentNumericId(dRec.doctype_id) ??
    parentDocTypeId;

  return {
    ...d,
    user_document_id: userDocId,
    case_document_id: caseDocId,
    case_doc_id: caseDocId,
    document_id: userDocId,
    doc_type_name: d.doc_type_name ?? cd.doc_type_name,
    doc_type_id: rowDocTypeId,
    doc_name: d.doc_name ?? derivedName,
    file_count:
      typeof d.file_count === "number"
        ? d.file_count
        : Array.isArray(d.files)
          ? d.files.length
          : docPath
            ? 1
            : 0,
    files: Array.isArray(d.files) ? d.files : undefined,
    uploaded_by: uploadedBy || (d.uploaded_by as unknown as string | undefined),
    uploaded_on: uploadedOn || d.uploaded_on,
  };
}

function flattenCaseDocuments(
  caseDocuments: CaseDocumentTypeItem[] | undefined
): CaseDocumentRow[] {
  if (!Array.isArray(caseDocuments)) return [];
  const rows: CaseDocumentRow[] = [];
  for (const cd of caseDocuments) {
    const list = cd.user_documents ?? cd.user_uploaded ?? [];
    for (const d of list) {
      rows.push(caseUploadToRow(cd, d));
    }
  }
  return rows;
}

export type CaseDocTypeTag = "personal" | "business";

export interface CaseDocRequirementSlot {
  doc_type_id: number;
  doc_type_name: string;
  doc_type_tag: CaseDocTypeTag;
  is_required: boolean;
  files: CaseDocumentRow[];
}

export function filterCaseDocumentsHiddenFromClient(
  rows: CaseDocumentRow[],
  isClientUser: boolean
): CaseDocumentRow[] {
  if (!isClientUser) return rows;
  return rows.filter((row) => !getCaseDocumentBizViewOnly(row));
}

export function filterCaseDocSlotsHiddenFromClient(
  slots: CaseDocRequirementSlot[],
  isClientUser: boolean
): CaseDocRequirementSlot[] {
  if (!isClientUser) return slots;
  const next: CaseDocRequirementSlot[] = [];
  for (const slot of slots) {
    const files = filterCaseDocumentsHiddenFromClient(slot.files, true);
    if (slot.files.length > 0 && files.length === 0) continue;
    next.push({ ...slot, files });
  }
  return next;
}

export function normalizeCaseDocTypeTag(tag?: string): CaseDocTypeTag {
  const t = (tag ?? "personal").toLowerCase();
  return t === "business" ? "business" : "personal";
}

export function coerceDocumentTypeIsOptional(flag: unknown): boolean {
  if (flag === true) return true;
  if (flag === 1 || flag === "1") return true;
  if (typeof flag === "string") {
    const s = flag.trim().toLowerCase();
    if (s === "true" || s === "yes" || s === "y") return true;
  }
  return false;
}

export function getCaseDocumentRequirementTypeId(cd: CaseDocumentTypeItem): number | undefined {
  const cdRec = cd as Record<string, unknown>;
  return (
    coerceDocumentNumericId(cd.doc_type_id) ??
    coerceDocumentNumericId(cdRec.document_type_id) ??
    coerceDocumentNumericId(cdRec.doctype_id)
  );
}

function resolveOptionalForCaseDocSlot(
  cd: CaseDocumentTypeItem,
  serviceDocTypeCatalog: DocumentTypeOption[]
): boolean {
  const rec = cd as Record<string, unknown>;
  const raw = rec.is_optional ?? rec.Is_Optional;
  if (raw !== undefined && raw !== null && raw !== "") {
    return coerceDocumentTypeIsOptional(raw);
  }
  const parentDocTypeId = getCaseDocumentRequirementTypeId(cd);
  if (parentDocTypeId !== undefined) {
    const st = serviceDocTypeCatalog.find((s) => s.doc_type_id === parentDocTypeId);
    if (st) return st.is_optional === true;
  }
  return true;
}

export function buildDocumentRequirementSlots(
  caseDocuments: CaseDocumentTypeItem[],
  serviceDocTypeCatalog: DocumentTypeOption[]
): CaseDocRequirementSlot[] {
  const catalogById = new Map<number, DocumentTypeOption>();
  for (const e of serviceDocTypeCatalog) catalogById.set(e.doc_type_id, e);

  const catalogLoaded = serviceDocTypeCatalog.length > 0;
  const slots: CaseDocRequirementSlot[] = [];

  for (const cd of caseDocuments) {
    const parentDocTypeId = getCaseDocumentRequirementTypeId(cd);
    if (parentDocTypeId === undefined) continue;

    const meta = catalogById.get(parentDocTypeId);
    if (catalogLoaded && meta && meta.is_active === false) continue;

    const list = cd.user_documents ?? cd.user_uploaded ?? [];
    const files = list.map((d) => caseUploadToRow(cd, d));

    let isOptional: boolean;
    if (catalogLoaded && meta && meta.is_active) {
      isOptional = meta.is_optional === true;
    } else if (catalogLoaded && !meta) {
      isOptional = cd.is_optional === true;
    } else {
      isOptional = resolveOptionalForCaseDocSlot(cd, serviceDocTypeCatalog);
    }

    slots.push({
      doc_type_id: parentDocTypeId,
      doc_type_name: cd.doc_type_name ?? "—",
      doc_type_tag: normalizeCaseDocTypeTag(cd.doc_type_tag),
      is_required: !isOptional,
      files,
    });
  }

  if (slots.length > 0) return slots;

  for (const st of serviceDocTypeCatalog) {
    if (!st.is_active) continue;
    slots.push({
      doc_type_id: st.doc_type_id,
      doc_type_name: st.doc_type_name,
      doc_type_tag: normalizeCaseDocTypeTag(st.doc_type_tag),
      is_required: st.is_optional !== true,
      files: [],
    });
  }
  return slots;
}

export interface DocumentTypeOption {
  doc_type_id: number;
  doc_type_name: string;
  doc_type_tag?: string;
  is_optional: boolean;
  is_active: boolean;
}

function mapRawServiceDocTypeToCatalogEntry(raw: unknown): DocumentTypeOption | null {
  if (!raw || typeof raw !== "object") return null;
  const dt = raw as Record<string, unknown>;
  const id =
    coerceDocumentNumericId(dt.document_type_id) ??
    coerceDocumentNumericId(dt.doc_type_id) ??
    coerceDocumentNumericId(dt.doctype_id) ??
    coerceDocumentNumericId(dt.id);
  if (id === undefined) return null;
  const nameCandidates = [
    dt.name,
    dt.doc_type_name,
    dt.document_type_name,
    dt.title,
    dt.type_name,
    dt.document_name,
    dt.label,
  ];
  let name = "";
  for (const c of nameCandidates) {
    if (c !== undefined && c !== null && String(c).trim() !== "") {
      name = String(c).trim();
      break;
    }
  }
  if (!name) name = `Document type ${id}`;
  const tagRaw = dt.doc_type_tag ?? dt.document_tag ?? dt.tag;
  const tag = typeof tagRaw === "string" ? tagRaw : undefined;
  return {
    doc_type_id: id,
    doc_type_name: name,
    doc_type_tag: tag,
    is_optional: dt.is_optional === true,
    is_active: isDocTypeActiveFlag(dt.is_active),
  };
}

export function mapServiceRawDocTypesToCatalog(rawList: unknown[]): DocumentTypeOption[] {
  const options: DocumentTypeOption[] = [];
  const seen = new Set<number>();
  for (const raw of rawList) {
    const opt = mapRawServiceDocTypeToCatalogEntry(raw);
    if (opt && !seen.has(opt.doc_type_id)) {
      seen.add(opt.doc_type_id);
      options.push(opt);
    }
  }
  return options;
}

export type FetchCaseDocumentsKind = "initial" | "soft";

export function useCaseDocuments(
  caseId: string | undefined,
  serviceId: string | number | undefined
) {
  const { business_id, device_token, session_id } = useSessionData();
  const { isClient, shortcode } = useViewerRoleContext();
  const isClientUser =
    isClient ||
    String(shortcode ?? "")
      .trim()
      .toLowerCase() === "client";
  const [loading, setLoading] = useState(false);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [caseDocuments, setCaseDocuments] = useState<CaseDocumentTypeItem[]>([]);
  const [serviceDocTypeCatalog, setServiceDocTypeCatalog] = useState<DocumentTypeOption[]>([]);

  const fetchDocuments = useCallback(
    async (kind: FetchCaseDocumentsKind = "initial") => {
      if (!caseId || !business_id || !device_token || !session_id) return;
      if (kind === "soft") {
        setListRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const res = await casesService.getCaseUploadedDocuments({
          case_id: caseId,
          business_id,
          device_token,
          session_id,
          include_upload_meta: true,
        });
        if (res?.data?.case_documents) {
          setCaseDocuments(res.data.case_documents);
        } else {
          setCaseDocuments([]);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load documents");
        setCaseDocuments([]);
      } finally {
        if (kind === "soft") {
          setListRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [caseId, business_id, device_token, session_id]
  );

  useEffect(() => {
    if (caseId) fetchDocuments("initial");
  }, [caseId, fetchDocuments]);

  useEffect(() => {
    if (
      serviceId === undefined ||
      serviceId === null ||
      serviceId === "" ||
      !business_id ||
      !device_token ||
      !session_id
    ) {
      setServiceDocTypeCatalog([]);
      return;
    }
    casesService
      .getServiceDocTypes({
        service_id: serviceId,
        business_id,
        device_token,
        session_id,
      })
      .then((res) => {
        const rawList = extractDocTypesArrayFromGetServiceResponse(res);
        setServiceDocTypeCatalog(mapServiceRawDocTypesToCatalog(rawList));
      })
      .catch(() => setServiceDocTypeCatalog([]));
  }, [serviceId, business_id, device_token, session_id]);

  const catalogByTypeId = useMemo(() => {
    const m = new Map<number, DocumentTypeOption>();
    for (const e of serviceDocTypeCatalog) m.set(e.doc_type_id, e);
    return m;
  }, [serviceDocTypeCatalog]);

  const flattenedDocuments = useMemo(() => {
    const flat = flattenCaseDocuments(caseDocuments);
    const byCatalog =
      serviceDocTypeCatalog.length === 0
        ? flat
        : flat.filter((row) => {
            const tid = getCaseDocumentTypeId(row);
            if (tid === undefined) return true;
            const meta = catalogByTypeId.get(tid);
            if (!meta) return true;
            return meta.is_active;
          });
    return filterCaseDocumentsHiddenFromClient(byCatalog, isClientUser);
  }, [caseDocuments, serviceDocTypeCatalog, catalogByTypeId, isClientUser]);

  const fallbackDocTypeOptions: DocumentTypeOption[] = caseDocuments
    .filter(
      (cd) =>
        cd.doc_type_id !== undefined &&
        cd.doc_type_id !== null &&
        cd.doc_type_name !== undefined &&
        cd.doc_type_name !== null
    )
    .map((cd) => {
      const rec = cd as Record<string, unknown>;
      const activeFromCase =
        isDocTypeActiveFlag(rec.is_active) && isDocTypeActiveFlag(cd.is_active);
      return {
        doc_type_id: cd.doc_type_id!,
        doc_type_name: cd.doc_type_name!,
        doc_type_tag: cd.doc_type_tag,
        is_optional: cd.is_optional === true,
        is_active: activeFromCase,
      };
    });

  const documentTypeOptions = useMemo(() => {
    if (serviceDocTypeCatalog.length === 0) {
      return fallbackDocTypeOptions.filter((o) => o.is_active);
    }
    const catalogById = new Map<number, DocumentTypeOption>();
    for (const o of serviceDocTypeCatalog) catalogById.set(o.doc_type_id, o);
    const merged = new Map<number, DocumentTypeOption>();
    for (const o of serviceDocTypeCatalog) {
      if (o.is_active) merged.set(o.doc_type_id, o);
    }
    for (const o of fallbackDocTypeOptions) {
      const catalogEntry = catalogById.get(o.doc_type_id);
      if (catalogEntry) continue;
      if (o.is_active) merged.set(o.doc_type_id, o);
    }
    return Array.from(merged.values());
  }, [serviceDocTypeCatalog, fallbackDocTypeOptions]);

  const documentSlots = useMemo(
    () =>
      filterCaseDocSlotsHiddenFromClient(
        buildDocumentRequirementSlots(caseDocuments, serviceDocTypeCatalog),
        isClientUser
      ),
    [caseDocuments, serviceDocTypeCatalog, isClientUser]
  );

  const refresh = useCallback(() => fetchDocuments("soft"), [fetchDocuments]);

  return {
    documents: flattenedDocuments,
    documentTypeOptions,
    documentSlots,
    loading,
    listRefreshing,
    error,
    refresh,
    isClientUser,
  };
}
